import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getAllDocsSnap,
  getDocDataSnap,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import SelectInput from "../Components/SelectInput";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseButton from "../../../components/CloseButton";
import CommentBlock from "../../../components/CommentBlock";
import { useCompany } from "../../../context/CompanyContext";
import TaskAssignmentModule from "../../../components/TaskAssignmentModule";
import HistoryModule from "../../../components/HistoryModule";
import {
  sendNotification,
  sendBulkNotifications,
} from "../../../utils/notifications";
import { storeNotification } from "../../../services/notificationService";
import {
  createWorkPermitStatusNotification,
  createWorkPermitCommentNotification,
  createWorkPermitAssignmentNotification,
} from "../../../utils/workPermitNotifications";
import { dualNotification } from "../../../utils/EleghoUtils";
import {
  FiEdit2,
  FiX,
  FiPaperclip,
  FiCheckCircle,
  FiClock,
} from "react-icons/fi";

export default function WorkPermitDetails({ application, setViewMode }) {
  const { id } = useParams();
  const currentUser = useSelector((state) => state.userObject);
  const { getTerm, getCapitalizedTerm } = useCompany();

  const [isLoading, setIsLoading] = React.useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [formData, setFormData] = React.useState(application);
  const [updateHappened, setUpdateHappened] = React.useState(false);
  const [status, setStatus] = React.useState(application.status);
  const [comments, setComments] = React.useState([]);
  const [newComment, setNewComment] = React.useState("");
  const [files, setFiles] = React.useState({});
  const [adminUsers, setAdminUsers] = React.useState([]);

  const [assignedToArray, setAssignedToArray] = React.useState(
    application.assignedTo || []
  );

  useEffect(() => {
    const fetchApplication = async () => {
      getDocDataSnap("workPermits", application.id, (data) => {
        setFormData(data);
        setFiles(data.files);
        setStatus(data.status);
        setComments(data.comments || []);
        setIsLoading(false);
      });
    };
    fetchApplication();
  }, [assignedToArray, application.id]);

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  const statusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Approved", value: "Approved" },
    { label: "Returned", value: "Returned" },
  ];

  const handleChange = (e) => {
    setNewComment(e.target.value);
  };

  const handleSelect = (selected) => {
    setUpdateHappened(true);
    setStatus(selected.value);
  };

  const handleUpdateStatus = async () => {
    setIsProcessing(true);

    try {
      const docObject = {
        ...formData,
        status,
        assignedTo: assignedToArray,
        approvedAt: status === "Approved" ? new Date().toLocaleString() : "",
        approvedBy: status === "Approved" ? currentUser.displayName : "",
        updatedBy: currentUser.email,
        updatedByName: currentUser.displayName,
        updatedAt: new Date().toLocaleString(),
        updateType: "Status is updated to " + status,
        history: [
          ...(formData?.history || []),
          {
            action: "Status Updated",
            by: currentUser.displayName,
            at: new Date().toLocaleString(),
            to: status,
          },
        ],
      };

      await setDocData("workPermits", application.id, docObject);

      const notificationObject = {
        recipient: formData.createdBy,
        recipientName: formData.name,
        sender: currentUser.email,
        senderName: currentUser.displayName,
        subject: "Work Permit Status Updated",
        message: `Your work permit request has been updated to ${status}`,
        type: "Work Permit Status Update",
        id: application.id,
        workPermitType: formData.workPermitType,
        status: status,
        url: `/request-details/${application.id}`,
        read: "false",
      };

      await dualNotification(notificationObject);

      toast.success("Status updated successfully");
      setUpdateHappened(false);
      setIsProcessing(false);
    } catch (error) {
      toast.error("Failed to update status");
      setIsProcessing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData(application);
    setAssignedToArray(application.assignedTo || []);
    toast.error("Edit Cancelled");
  };

  const handleAddComment = async () => {
    if (!newComment || newComment.trim() === "") {
      toast.error("Comment cannot be empty");
      return;
    }

    const comment = {
      message: newComment,
      createdAt: new Date().toLocaleString(),
      createdBy: currentUser.displayName,
      email: currentUser.email,
    };

    const docObject = {
      ...formData,
      comments: [...comments, comment],
      updatedBy: currentUser.email,
      updatedByName: currentUser.displayName,
      updatedAt: new Date().toLocaleString(),
      lastUpdateType: "Comment Added",
    };

    try {
      await setDocData("workPermits", application.id, docObject);

      const notificationObject = {
        recipient: formData.createdBy,
        recipientName: formData.name,
        sender: currentUser.email,
        senderName: currentUser.displayName,
        subject: "Work Permit - Comment Added",
        message: `A new comment has been added to your work permit request`,
        type: "Work Permit Comment",
        id: application.id,
        workPermitType: formData.workPermitType,
        status: status,
        url: `/request-details/${application.id}`,
        read: "false",
      };

      await dualNotification(notificationObject);
      toast.success("Comment added successfully");
      setComments([...comments, comment]);
      setNewComment("");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleDeleteComment = async (index) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      const newComments = comments.filter((comment, i) => i !== index);

      const docObject = {
        ...formData,
        comments: newComments,
        updatedBy: currentUser.email,
        updatedByName: currentUser.displayName,
        updatedAt: new Date().toLocaleString(),
        lastUpdateType: "Comment Deleted",
      };

      try {
        await setDocData("workPermits", application.id, docObject);
        toast.success("Comment deleted successfully");
        setComments(newComments);
      } catch (error) {
        toast.error("Failed to delete comment");
      }
    }
  };

  const handleSaveAssignedTo = async () => {
    if (!editMode) return;

    setIsProcessing(true);
    const docObject = {
      ...formData,
      assignedTo: assignedToArray,
      updatedBy: currentUser.email,
      updatedByName: currentUser.displayName,
      updatedAt: new Date().toLocaleString(),
      history: [
        ...(formData?.history || []),
        {
          action: "Assigned to",
          by: currentUser.displayName,
          at: new Date().toLocaleString(),
          to: assignedToArray.map((item) => item.label).join(", "),
        },
      ],
    };

    try {
      await setDocData("workPermits", application.id, docObject);

      // Send notifications to newly assigned users
      const previouslyAssigned = new Set(
        (formData.assignedTo || []).map((user) => user.value)
      );
      const newlyAssigned = assignedToArray
        .filter((user) => !previouslyAssigned.has(user.value))
        .map((user) => user.value);

      if (newlyAssigned.length > 0) {
        await Promise.all(
          newlyAssigned.map(async (userEmail) => {
            const notificationObject = {
              recipient: userEmail,
              recipientName: userEmail,
              sender: currentUser.email,
              senderName: currentUser.displayName,
              subject: "Work Permit Assigned",
              message: `You have been assigned a new work permit request`,
              type: "Work Permit Assignment",
              id: application.id,
              workPermitType: formData.workPermitType,
              status: status,
              url: `/property-management?type=Work+Permits`,
              read: "false",
            };

            await dualNotification(notificationObject);
          })
        );
      }

      toast.success("Assigned successfully");
      setIsProcessing(false);
      setEditMode(false);
      setUpdateHappened(false);
    } catch (error) {
      toast.error("Failed to assign");
      setIsProcessing(false);
    }
  };

  const filesArray =
    files !== undefined
      ? Object.keys(files).map((key) => ({
          name: key,
          url: files[key],
        }))
      : [];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "returned":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return <FiCheckCircle className="w-4 h-4" />;
      case "returned":
        return <FiX className="w-4 h-4" />;
      case "pending":
        return <FiClock className="w-4 h-4" />;
      default:
        return <FiClock className="w-4 h-4" />;
    }
  };

  const allowedToEdit = ["admin", "super-admin"];

  return (
    <div className="min-h-screen p-4 sm:p-6  w-full">
      <div className="max-w-7xl mx-auto relative">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
            <div className="text-base sm:text-lg font-semibold text-[#363062]">
              Work Permit Details
            </div>
            <div className="flex items-center gap-2 sm:gap-4 mr-12">
              {allowedToEdit.some((role) =>
                currentUser.roles.includes(role)
              ) && (
                <button
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    editMode
                      ? "bg-red-500 text-white hover:bg-red-600 shadow-sm"
                      : "bg-[#363062] text-white hover:bg-opacity-90 shadow-sm"
                  }`}
                  onClick={() => {
                    if (editMode) {
                      handleCancelEdit();
                    } else {
                      setEditMode(true);
                    }
                  }}
                >
                  {editMode ? (
                    <>
                      <FiX className="w-4 h-4" />
                      Cancel Edit
                    </>
                  ) : (
                    <>
                      <FiEdit2 className="w-4 h-4" />
                      Edit Work Permit
                    </>
                  )}
                </button>
              )}

              <button
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors absolute top-2 right-2"
                onClick={() => setViewMode(false)}
              >
                <FiX className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 sm:mb-6">
            <div
              className={`px-3 py-1.5 rounded-full border flex items-center gap-2 ${getStatusColor(
                status
              )} font-medium capitalize text-xs sm:text-sm`}
            >
              {getStatusIcon(status)}
              {status}
            </div>
            {editMode && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <SelectInput
                  name={"status"}
                  placeholder={"Select Status"}
                  options={statusOptions}
                  onChange={(selected) => handleSelect(selected)}
                  value={statusOptions.find((item) => item.value === status)}
                  className="w-full sm:w-48"
                />
                <button
                  className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 text-sm"
                  disabled={!updateHappened || isProcessing}
                  onClick={handleUpdateStatus}
                >
                  {isProcessing ? "Processing..." : "Update Status"}
                </button>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Permit Details */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Permit Information Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Work Permit Information
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Identifier
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.identifier || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Request Type
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.workPermitType || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Duration of Activities
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.duration || "N/A"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Start Date
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {new Date(formData?.startDate).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "long", day: "2-digit" }
                        ) || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Requested By
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.name || "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Additional details */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
                      <div className="text-xs sm:text-sm text-gray-500 mb-2">
                        Description of Work
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.description || "N/A"}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
                      <div className="text-xs sm:text-sm text-gray-500 mb-2">
                        Vehicle Information
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.vehicle || "N/A"}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
                      <div className="text-xs sm:text-sm text-gray-500 mb-2">
                        Workers List
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData?.workers || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062] flex items-center gap-2">
                    <FiPaperclip className="w-4 h-4" />
                    Documents
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  {filesArray && filesArray.length > 0 ? (
                    <div className="space-y-3">
                      {filesArray.map((file, index) => (
                        <div
                          key={index}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <div className="font-medium text-xs sm:text-sm mb-2 sm:mb-0">
                            {file.name}
                          </div>
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium text-xs sm:text-sm text-center"
                          >
                            View Document
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center p-4 sm:p-6 text-gray-500 italic text-xs sm:text-sm">
                      No documents uploaded
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Comments
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="mb-4 sm:mb-6">
                    <textarea
                      name="newComment"
                      className="w-full rounded-lg p-3 sm:p-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-transparent resize-none text-xs sm:text-sm"
                      rows="3"
                      onChange={handleChange}
                      placeholder="Write your comment here..."
                      value={newComment}
                    ></textarea>
                    <div className="flex justify-end mt-3">
                      <button
                        className="px-3 sm:px-4 py-2 bg-[#8FCD00] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm text-xs sm:text-sm"
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                      >
                        Submit Comment
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {comments.length > 0 ? (
                      comments.map((comment, index) => (
                        <CommentBlock
                          key={index}
                          comment={comment}
                          currentUser={currentUser}
                          handleDeleteComment={() => handleDeleteComment(index)}
                        />
                      ))
                    ) : (
                      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg text-center text-xs sm:text-sm">
                        No comments yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Assignment & History */}
            <div className="space-y-4 sm:space-y-6">
              {/* Assignment Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Task Assignment
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <TaskAssignmentModule
                    assignedToArray={assignedToArray}
                    setAssignedToArray={setAssignedToArray}
                    editMode={editMode}
                  />
                  {editMode && (
                    <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-end">
                      <button
                        className="px-3 sm:px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm text-xs sm:text-sm disabled:opacity-70"
                        onClick={handleSaveAssignedTo}
                        disabled={isProcessing}
                      >
                        {isProcessing ? "Processing..." : "Save Assignment"}
                      </button>
                      <button
                        className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-xs sm:text-sm"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* History Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    History
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="mt-4 flex flex-col-reverse w-full">
                    {formData?.history?.map((history, index) => (
                      <div
                        key={index}
                        className="relative border-l border-gray-300"
                      >
                        <div className="flex items-center relative -left-4">
                          <div className="bg-white p-1 rounded-full text-green-500">
                            <CheckCircleOutlineIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>
                          <div className="text-xs sm:text-sm">
                            {history.action} {history.to}
                          </div>
                        </div>
                        <div className="pl-4 mb-4">
                          <div className="text-xs text-gray-400">
                            {history.at}
                          </div>
                          <div className="text-xs font-semibold text-gray-400">
                            {history.by}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
